import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import {
  DATA_REFRESH_RATE_IN_MILLIS_LONG,
  MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS,
} from 'src/utils/constants/constants';
import { isInSearchPage } from 'src/utils/deepLinkUtils';
import { createAggregatedPurchaseOrder } from 'src/utils/purchaseUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  PurchaseClient,
  PurchaseOrder,
  PurchaseOrderQuery,
  PurchaseOrderQueryV2,
  PurchaseViewMode,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useClientLogContext } from '../ClientLogContext';
import { useFilterQueryContext } from '../FilterQueryContext';
import {
  getStopwatchGapsForCatalogPageLoad,
  getStopwatchLoggingId,
  StopwatchLoggingEventName,
  useStopwatchLoggingContext,
} from '../StopwatchLoggingContext';
import { PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY } from './PurchaseDataContext.constants';
import { buildV2FilterQuery } from './useGetAllPurchasesQuery.utils';

export const useGetAllPurchasesQuery = (
  useReadonlyDB?: boolean,
  viewModeOverride?: PurchaseViewMode,
  ignoreMaxCount?: boolean,
  doNotQueryForPurchases?: boolean,
  isForSearch?: boolean,
  isForSearchV2?: boolean
) => {
  const { activeAccountWebClientConfig, mainRoute } = useAppContext();

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Purchase
  );

  const { filterQuery } = useFilterQueryContext<
    PurchaseOrderQuery | PurchaseOrderQueryV2
  >();
  const { trackError } = useClientLogContext();
  const { allEventIds, totalPurchaseCount, logKey } = useCatalogDataContext();
  const { addStopGap } = useStopwatchLoggingContext();

  const isSearchPage = isInSearchPage(mainRoute);

  const moddedFilterQuery = useMemo(
    () => ({
      ...filterQuery,
      // This is not used by server
      searchText: null,
      sortBy: null,
      isSortDescending: null,
      viewMode: null,
      // These will be provided during query
      eventOrMappingIds: null,
      performerIds: null,
      venueIds: null,
    }),
    [filterQuery]
  );

  const moddedFilterQueryForSummary = useMemo(
    () => ({
      ...moddedFilterQuery,
      // Remove pagination related fields
      searchOptions: null,
    }),
    [moddedFilterQuery]
  );

  const filterPurchaseOrders = useCallback(
    (purchaseOrders: PurchaseOrder[] | null | undefined) => {
      if (purchaseOrders && filterQuery.purchaseOrderState) {
        return purchaseOrders.filter(
          (po) => po.poState === filterQuery.purchaseOrderState
        );
      }

      return purchaseOrders;
    },
    [filterQuery.purchaseOrderState]
  );

  const shouldQuery =
    !doNotQueryForPurchases &&
    !(
      viewModeOverride !== PurchaseViewMode.FlattenedView ||
      (viewModeOverride === PurchaseViewMode.FlattenedView &&
        !ignoreMaxCount &&
        (!totalPurchaseCount ||
          totalPurchaseCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS)) ||
      activeAccountWebClientConfig.activeAccountId == null
    );

  const shouldQuerySummary = shouldQuery && isForSearchV2;

  const allPurchasesQuery = useQuery({
    queryKey: [
      PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY,
      moddedFilterQuery,
      allEventIds, // V2 also uses allEventIds as a fallback when the user has no explicit event/performer/venue filter
      isForSearch,
      isForSearchV2,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      // XXX Workaround for the backend
      // because backend treats empty arrays as "no filter" (get all) for backwards compatibility
      // so we require either eventIds, entityIds, or marketplaceEntityIds
      // as a minimum
      if (
        !isForSearchV2 &&
        !allEventIds?.length &&
        !moddedFilterQuery.entityIds?.length &&
        !moddedFilterQuery.marketplaceEntityIds
      ) {
        return {
          items: [],
          summary: null,
          pitId: null,
          totalItemsCount: null,
          searchAfter: null,
        };
      }

      const filterQueryWithEventIds = {
        ...moddedFilterQuery,
        eventOrMappingIds: (allEventIds ?? []).map((ev) => ev.viagVirtId),
        performerIds: (allEventIds ?? [])
          .filter((ev) => ev.performerId != null)
          .map((ev) => ev.performerId!),
        venueIds: (allEventIds ?? []).map((ev) => ev.venueId),
      };

      return tryInvokeApi(
        async () => {
          const client = new PurchaseClient(activeAccountWebClientConfig);

          if (isForSearchV2) {
            // Re-add catalog filter fields that were nulled out in moddedFilterQuery.
            // When the user has no explicit event/performer/venue filter we fall back to
            // allEventIds (catalog event window) so that V2 results are scoped the same
            // way V1 results are.
            const v2FilterQuery = buildV2FilterQuery(
              moddedFilterQuery,
              filterQuery,
              allEventIds
            );

            const searchResult = await client.searchPurchaseOrdersV2(
              v2FilterQuery as PurchaseOrderQueryV2
            );

            return {
              items: searchResult.entities.items,
              summary: searchResult.entities.summary,
              pitId: searchResult.pitId,
              searchAfter: searchResult.searchAfter,
              totalItemsCount: searchResult.totalItemsCount,
              indexLastUpdatedAt: searchResult.indexLastUpdatedAt,
              sortedItemIds: searchResult.sortedItemIds,
            };
          }

          const data = isForSearch
            ? await client.searchPurchaseOrders(
                filterQueryWithEventIds,
                useReadonlyDB
              )
            : await client.getPurchaseOrders(
                filterQueryWithEventIds,
                useReadonlyDB
              );

          return data;
        },
        (error) => {
          trackError(
            PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY,
            error,
            filterQueryWithEventIds
          );
        },
        () => {
          if (isSearchPage) {
            addStopGap(
              logKey,
              getStopwatchLoggingId(mainRoute),
              getStopwatchGapsForCatalogPageLoad(false, true),
              StopwatchLoggingEventName.EventEntitiesLoad,
              ActionOutboxEntityType.Purchase
            );
          }
        }
      );
    },
    enabled: shouldQuery,
    staleTime: Infinity, // Since we're always refetching on an interval, we don't want query to calculate whether the data is stale
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const purchasesSummaryQuery = useQuery({
    queryKey: [
      PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY,
      moddedFilterQueryForSummary,
      allEventIds, // V2 summary also depends on allEventIds as a fallback when no explicit user filter
      isForSearchV2,
    ],
    queryFn: async () => {
      if (!shouldQuerySummary) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new PurchaseClient(activeAccountWebClientConfig);

          // Re-add catalog filter fields that were nulled out in moddedFilterQuery.
          // When the user has no explicit event/performer/venue filter we fall back to
          // allEventIds (catalog event window) so that V2 summary totals match V1.
          const v2FilterQuery = buildV2FilterQuery(
            moddedFilterQuery,
            filterQuery,
            allEventIds
          );

          const searchResult = await client.searchPurchaseOrdersV2Summary(
            v2FilterQuery as PurchaseOrderQueryV2
          );

          return searchResult == null ? null : searchResult;
        },
        (error) => {
          trackError(PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY, error);
        }
      );
    },
    enabled: shouldQuerySummary,
    staleTime: Infinity, // Since we're always refetching on an interval, we don't want query to calculate whether the data is stale
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const allPurchasesFiltered = useMemo(() => {
    return filterPurchaseOrders(allPurchasesQuery.data?.items);
  }, [allPurchasesQuery.data?.items, filterPurchaseOrders]);

  const allPurchasesSummary = useMemo(() => {
    if (isForSearchV2) {
      return purchasesSummaryQuery.data ?? allPurchasesQuery.data?.summary;
    }

    if (!hasTableTotalsFeature) {
      return null;
    }

    if (allPurchasesFiltered?.length != allPurchasesQuery.data?.items?.length) {
      return createAggregatedPurchaseOrder(allPurchasesFiltered);
    }

    if (isSearchPage) {
      // TODO: Remove this once TableTotals_All/TableTotals_Purchase is GA'ed
      // Need the fallback for the case where the salesSummaryByEvent is null
      // because without TableTotals_* feature flag, the salesSummaryByEvent is null
      // but we still want to show the totals on search page
      return (
        allPurchasesQuery.data?.summary ??
        createAggregatedPurchaseOrder(allPurchasesFiltered)
      );
    }

    return allPurchasesQuery.data?.summary;
  }, [
    allPurchasesFiltered,
    allPurchasesQuery.data?.items?.length,
    allPurchasesQuery.data?.summary,
    hasTableTotalsFeature,
    isForSearchV2,
    isSearchPage,
    purchasesSummaryQuery.data,
  ]);

  return {
    allPurchasesQuery,
    allPurchasesSummaryQuery: purchasesSummaryQuery,
    allPurchasesFiltered,
    allPurchasesSummary,
  };
};
